<template lang="pug">
.product-tile
  .product-tile__image-wrapper(@mouseover="hover" @mouseleave="leave")
    .product-tile__badge(v-if="!hovered")
      .product-tile__badge--out-stock(v-if="product.settings.labels.outStock && isOutStock") sold out
      .product-tile__badge--sale(v-else-if="product.settings.labels.onSale && isOnSale") on sale
      .product-tile__badge--new(v-else-if="product.settings.labels.newProduct && isNew") new
      .product-tile__badge--bestseller(v-else-if="product.settings.labels.bestSeller && isBestseller") best seller
      .product-tile__badge--custom(v-else-if="product.settings.labels.custom && hasCustomBadge != null" v-html="hasCustomBadge")
    img.product-tile__image(:src="getDefaultImage" :alt="product.title")
    transition(name="fade")
      .product-tile__image-background(
          v-if="hovered && $mq !== 'mobile'"
          role="img"
          :style="`background-image: url(${getHoverBackgroundImage})`")
        .product-tile__image-foreground(
          role="img"
          :style="`background-image: url(${getHoverForegroundImage})`")
        a.product-tile__cta(v-if="hovered &&  $mq !== 'mobile'" :href="url" aria-label="Add to Cart") Add to Cart
  .product-tile__info
    h3.product-tile__title(v-if="product.title" v-html="product.title")
    p.product-tile__excerpt(v-if="product.excerpt" v-html="product.excerpt")
    p.product-tile__price
      span.product-tile__price--compare(v-if="comparePrice") {{comparePrice}}
      span.product-tile__price--divider(v-if="comparePrice") |
      span.product-tile__price--sale(v-if="price" v-html="price")
  .product-tile__swatches(v-if="product.variants_count > 1")
    a.product-tile__swatch(
      href="#"
      v-for="variant in colorVariantProducts"
      :aria-label="`${product.title} ${variant.title}`"
      @click.prevent.stop="updateVariantProduct(variant.id)"
      :key="variant.id"
      :class="{active: activeVariant == variant.id}"
      :style="`background-color: ${variant.color_swatch}`")
      icon(name="diamond-fill" size="12px")
  a.product-tile__cta.product-tile__cta--mobile(v-if="$mq === 'mobile'" :href="url" aria-label="Add to Cart") Add to Cart
</template>

<script>
  import Icon from 'scripts/components/basic/Icon.vue';
  import { money } from 'scripts/filters/money.js';

  export default {
    name: 'ProductTile',
    components: { Icon },
    props: {
      product: Object
    },
    data() {
      return {
        url: null,
        price: null,
        hovered: false,
        comparePrice: null,
        activeVariant: null,
        imageAltText: '',
        defaultImage: '',
        hoverBackgroundImage: '',
        hoverForegroundImage: ''
      }
    },
    computed: {
      hasVariant() {
        return this.product.variants_count > 1
      },
      getDefaultImage() {
        return (this.defaultImage != '')
          ? this.defaultImage
          : this.product.settings.tilePlaceholder
      },
      getHoverBackgroundImage() {
        return (this.hoverBackgroundImage != '')
          ? this.hoverBackgroundImage
          : this.product.settings.tilePlaceholder
      },
      getHoverForegroundImage() {
        return (this.hoverForegroundImage != '')
          ? this.hoverForegroundImage
          : this.product.settings.tilePlaceholder
      },
      colorVariantProducts() {
        if(this.hasVariant) {
          return this.product.variants.filter((variant) => variant.color_swatch != null && variant.color_swatch != '')
        }
      },
      isOutStock() {
        if(this.hasVariant) {
          return !this.product.variants.some((variant) => variant.inventory_quantity)
        }
        return !this.product.available
      },
      isOnSale() {
        if(this.hasVariant) {
          return this.product.variants.some((variant) => variant.compare_at_price > variant.price)
        }
        return this.product.compare_at_price > this.product.price
      },
      isNew() {
        return this.product.tags.some((tag) => tag.includes('badge::new'))
      },
      isBestseller() {
        return this.product.tags.some((tag) => tag.includes('badge::bestseller'))
      },
      hasCustomBadge() {
        const custom_badge = this.product.tags.filter((tag) => tag.includes("custom_badge::"))
        if (custom_badge != undefined && custom_badge != '') {
          return String(custom_badge[0]).replace('custom_badge::', '')
        }
      }
    },
    methods: {
      hover() {
        if(this.$mq !== 'mobile') {
          this.hovered = true
        }
      },
      leave() {
        if(this.$mq !== 'mobile') {
          this.hovered = false
        }
      },
      updateNonVariant() {
        this.url = this.product.url
        this.price = money(this.product.price)
        this.comparePrice = this.product.compare_at_price > this.product.price
          ? money(this.product.compare_at_price)
          : null
        this.imageAltText = this.product.images.default[0].alt
        this.defaultImage = this.product.images.default[0].sizes.md
        this.hoverBackgroundImage = this.product.images.background.sizes.sm
        this.hoverForegroundImage = this.product.images.foreground.sizes.sm
      },
      updateVariantProduct(variant_id) {
        const variant = this.colorVariantProducts.filter((variant) => variant.id == variant_id)
        this.url = variant[0].url
        this.activeVariant = variant[0].id
        this.price = (variant[0].price) ? money(variant[0].price) : money(product.price)
        this.comparePrice = variant[0].compare_at_price > variant[0].price
          ? money(variant[0].compare_at_price)
          : null
        this.imageAltText = this.product.images.default.alt
        this.defaultImage = variant[0].images.default.sizes.md
        this.hoverBackgroundImage = variant[0].images.background.sizes.sm
        this.hoverForegroundImage = variant[0].images.foreground.sizes.sm
      }
    },
    created() {
      if(this.hasVariant) {
        this.updateVariantProduct(this.colorVariantProducts[0].id)
      } else {
        this.updateNonVariant()
      }
    }
  }
</script>

<style lang="scss">
  .product-tile {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  // Badge
  .product-tile__badge {
    position: absolute;
    top: 16px;
    left: 0;
    border-top-right-radius: 30px;
    border-bottom-right-radius: 30px;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 1px;
    overflow: hidden;
    font-family: $secondary-font-family;

    @include tablet-up() {
      font-size: 12px;
      top: 40px;
    }
  }
  .product-tile__badge--out-stock,
  .product-tile__badge--sale,
  .product-tile__badge--new,
  .product-tile__badge--bestseller,
  .product-tile__badge--custom {
    background-color: var(--color-slate-500);
    color: var(--color-white);
    padding: 6px 12px 6px 10px;

    @include tablet-up() {
      padding: 8px 20px 8px 16px;
    }
  }
  .product-tile__badge--bestseller {
    background-color: var(--color-blue-500);
    color: var(--color-navy-500);
  }
  .product-tile__badge--out-stock {
    background-color: var(--color-grey-500);
    color: var(--color-navy-500);
  }

  // Product Images
  .product-tile__image-wrapper {
    display: block;
    position: relative;

    @include tablet-up {
      cursor: pointer;
    }
  }
  .product-tile__image {
    display: block;
  }

  .product-tile__image-wrapper,
  .product-tile__image,
  .product-tile__image-background,
  .product-tile__image-foreground {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    box-sizing: border-box;
    overflow: hidden;
    border-radius: 15px;

    @include tablet-up {
      border-radius: 30px;
    }
  }
  .product-tile__image-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 20px;
    display: flex;
    flex-direction: column;
  }
  .product-tile__image-foreground {
    width: 100%;
    flex: 1;
    margin-bottom: 20px;
    border-radius: 30px;
  }

  // CTA - add to cart
  .product-tile__cta,
  .product-tile__cta:link,
  .product-tile__cta:visited {
    border-radius: 10px;
    border: 1.5px solid $color-black;
    color: $color-black;
    background-color: $color-white;
    padding: 12px 10px;
    font-size: rem(12px);
    font-family: $secondary-font-family;
    font-weight: 400;
    line-height: rem(13px);
    letter-spacing: 2px;
    text-transform: uppercase;
    text-decoration: none;
    text-align: center;

    @include tablet-up() {
      font-size: rem(13px);
      padding: 14px 16px;
    }

    &:hover,
    &:active {
      color: $color-white;
      border: 1.5px solid $color-white;
      background-color: $color-black;
    }

  }
  .product-tile__cta--mobile,
  .product-tile__cta--mobile:link,
  .product-tile__cta--mobile:visited {
    background-color: transparent;
    border: 1.5px solid $color-white;
    color: $color-white;
    margin-top: auto;

    &:hover,
    &:active {
      background-color: $color-white;
      color: $color-black;
    }
  }

   // Product Info
  .product-tile__info {
    padding-top: rem(8px);
    font-size: $paragraph-font-size;
    font-weight: $font-weight-regular;
    text-align: left;

    @include tablet-up() {
      padding-top: $spacing-unit;
    }
  }
  .product-tile__title {
    font-size: rem(14px);
    text-transform: uppercase;
    margin: 0;

    @include tablet-up() {
      font-size: $paragraph-font-size;
    }
  }
  .product-tile__excerpt {
    margin: 0 0 rem(16px) 0;
    line-height: 120%;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    font-size: rem(14px);

    @include tablet-up() {
      font-size: $paragraph-font-size;
    }
  }

  // Product Price
  .product-tile__price {
    display: flex;
    align-items: center;

    @include mobile-only() {
      font-size: rem(14px);
    }
  }
  .product-tile__price--compare {
    text-decoration: line-through;
  }
  .product-tile__price--divider {
    margin: 0 rem(10px);
  }

  // Swatch
  .product-tile__swatches {
    display: flex;
    padding: 0 0 15px 5px;

    @include tablet-up() {
      padding: 0 0 5px 5px;
    }
  }
  .product-tile__swatch {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    cursor: pointer;
    position: relative;
    color: $color-white;

    .icon,
    &:after {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    &:after {
      content: "";
      width: 21px;
      height: 21px;
      border-radius: 50%;
    }
    &:hover,
    &:active,
    &.active {
      &:after { border: 1px solid $color-white; }
    }
    &:not(:last-child) {
      margin-right: 14px;
    }
  }
</style>
